import { TauxTva, TVA_RATES } from '../types';

/**
 * Format amount in Tunisian Dinars with 3 decimal places
 * e.g., 1234567.891 -> "1 234 567.891 TND"
 */
export function formatTND(amount: number | undefined | null): string {
  if (amount === undefined || amount === null) return '0.000 TND';
  const fixed = amount.toFixed(3);
  const [intPart, decPart] = fixed.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedInt}.${decPart} TND`;
}

/**
 * Format date to DD/MM/YYYY
 */
export function formatDate(date: string | Date | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format date to YYYY-MM-DD for input fields
 */
export function formatDateISO(date: string | Date | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

/**
 * Format matricule fiscal for display
 * Input: 1234567ABC123 -> 1234567/A/B/C/123
 */
export function formatMatriculeFiscal(mf: string | undefined | null): string {
  if (!mf) return '';
  const cleaned = mf.replace(/[^A-Za-z0-9]/g, '');
  if (cleaned.length !== 13) return mf;
  return `${cleaned.slice(0, 7)}/${cleaned[7]}/${cleaned[8]}/${cleaned[9]}/${cleaned.slice(10)}`;
}

/**
 * Calculate line item totals
 */
export function calculateLineTotals(
  quantite: number,
  prixUnitaireHT: number,
  tauxTva: TauxTva,
  fodecApplicable: boolean,
  remise: number = 0,
  tauxFodec: number = 1
): {
  montantHT: number;
  montantTva: number;
  montantFodec: number;
  montantTTC: number;
  montantRemise: number;
} {
  const montantBrut = quantite * prixUnitaireHT;
  const montantRemise = montantBrut * (remise / 100);
  const montantHT = montantBrut - montantRemise;

  // Le FODEC (1%) s'ajoute au HT et entre dans l'assiette de la TVA :
  // TVA = (HT + FODEC) × taux  — identique au backend (DocumentService.buildLigne).
  const montantFodec = fodecApplicable ? montantHT * (tauxFodec / 100) : 0;
  const base = montantHT + montantFodec;
  const tvaRate = TVA_RATES[tauxTva] / 100;
  const montantTva = base * tvaRate;
  const montantTTC = base + montantTva;

  return {
    montantHT: Math.round(montantHT * 1000) / 1000,
    montantTva: Math.round(montantTva * 1000) / 1000,
    montantFodec: Math.round(montantFodec * 1000) / 1000,
    montantTTC: Math.round(montantTTC * 1000) / 1000,
    montantRemise: Math.round(montantRemise * 1000) / 1000,
  };
}

/**
 * Calculate document totals from line items
 */
export function calculateDocumentTotals(
  lignes: Array<{
    montantHT: number;
    montantTva: number;
    montantFodec: number;
    montantRemise?: number;
  }>,
  typeDocument: string,
  tauxRetenueSource: number = 0,
  timbreFiscalMontant: number = 1.000,
  appliquerTimbreFiscal: boolean = true
): {
  totalHT: number;
  totalTva: number;
  totalFodec: number;
  totalRemise: number;
  timbreFiscal: number;
  totalTTC: number;
  retenueSource: number;
  netAPayer: number;
} {
  const totalHT = lignes.reduce((sum, l) => sum + l.montantHT, 0);
  const totalTva = lignes.reduce((sum, l) => sum + l.montantTva, 0);
  const totalFodec = lignes.reduce((sum, l) => sum + l.montantFodec, 0);
  const totalRemise = lignes.reduce((sum, l) => sum + (l.montantRemise ?? 0), 0);

  // Droit de timbre : dû sur les factures et les devis (tous modes de paiement),
  // pas sur les avoirs ni les bons de livraison, et pas non plus quand l'utilisateur l'a
  // désactivé sur le document (opération exonérée). Aligné sur DocumentService.computeTotals.
  const timbreFiscal =
    appliquerTimbreFiscal && (typeDocument === 'FACTURE' || typeDocument === 'DEVIS')
      ? timbreFiscalMontant
      : 0;
  // Le TTC est hors timbre (HT + FODEC + TVA) ; la retenue à la source porte sur le TTC ;
  // le timbre est ajouté au net à payer.
  const totalTTC = totalHT + totalFodec + totalTva;
  const retenueSource = totalTTC * (tauxRetenueSource / 100);
  const netAPayer = totalTTC + timbreFiscal - retenueSource;

  return {
    totalHT: Math.round(totalHT * 1000) / 1000,
    totalTva: Math.round(totalTva * 1000) / 1000,
    totalFodec: Math.round(totalFodec * 1000) / 1000,
    totalRemise: Math.round(totalRemise * 1000) / 1000,
    timbreFiscal: Math.round(timbreFiscal * 1000) / 1000,
    totalTTC: Math.round(totalTTC * 1000) / 1000,
    retenueSource: Math.round(retenueSource * 1000) / 1000,
    netAPayer: Math.round(netAPayer * 1000) / 1000,
  };
}

/**
 * Get number of days between two dates
 */
export function daysBetween(date1: string | Date, date2: string | Date): number {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format a number with thousands separator
 */
export function formatNumber(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
